import * as v from 'valibot'
import { NovelPlayConfig } from './play-config.js'

// 互动小说发布到正式环境（C25）：Stage 后台接口、Main 内部接口与两端发布记录的形状。
const Id = v.pipe(v.string(), v.minLength(1), v.maxLength(64))
const ReleaseId = v.pipe(v.string(), v.uuid())
const Count = v.pipe(v.number(), v.integer(), v.minValue(0))
const Sha256 = v.pipe(v.string(), v.regex(/^[a-f0-9]{64}$/))

export const NovelReleaseStatus = v.picklist(['prepared', 'transferring', 'applying', 'succeeded', 'failed'])

/** POST /api/novel/admin/packs/:id/release（admin）：要推到 Main 的版本，必须是这部作品的版本。 */
export const NovelReleaseBody = v.strictObject({ version_id: Id })

/**
 * 发布记录的一条日志：记录状态变化与这条日志在同一事务写入；stage 是写日志时的记录状态。日志存日志码与参数，
 * 由后台按码翻译（R-6）：locked（Stage 锁定版本）与 received（Main 收到发布包）带 version、assets；sending（Stage 开始发送）、
 * syncing（Main 开始同步图片）不带参数；writing（Main 开始写入）带 copied、reused；switched（已切换版本）带 version；
 * failed 带 code（失败原因码）与 message（原因原文）。此前写入的日志只有中文原句：code 为 null、params 为空，message 原样显示；
 * 新日志的 message 为 null。
 */
export const NovelReleaseLog = v.object({
  stage: NovelReleaseStatus,
  code: v.nullable(v.picklist(['locked', 'received', 'sending', 'syncing', 'writing', 'switched', 'failed'])),
  params: v.record(v.string(), v.union([v.string(), v.number()])),
  message: v.nullable(v.string()),
  created_at: v.string(),
})

/** 资产统计（stats_json）：本次发布去重后的对象数与字节、Main 新上传与按 hash 复用的对象数、新上传字节。 */
export const NovelReleaseStats = v.object({
  assets: Count,
  asset_bytes: Count,
  assets_copied: Count,
  assets_reused: Count,
  bytes_copied: Count,
})

/** 发起发布的管理员：邮箱与显示名取账号当前值，账号已不存在（或没设）时为 null，user_id 仍在。 */
export const NovelReleaseOperator = v.object({
  user_id: Id,
  email: v.nullable(v.string()),
  display_name: v.nullable(v.string()),
})

/**
 * 一条发布记录。Stage 为 outbound（pack_id、version_id 是 Stage 的作品与版本）；Main 为 inbound（作品按 slug
 * 建或更新，pack_id、version_id 是 Main 的行，套用成功前为 null）。requested_by 是 Stage 发起发布的管理员
 * （服务端已把账号 id 换成身份），Main 侧为 null。失败时 error_code、error_message 是失败原因：Main 拒绝的，
 * 记 Main 自己说的 code 与 message。
 */
export const NovelReleaseRecord = v.object({
  release_id: ReleaseId,
  direction: v.picklist(['outbound', 'inbound']),
  pack_id: v.nullable(Id),
  pack_slug: v.string(),
  version_id: v.nullable(Id),
  version: v.string(),
  requested_by: v.nullable(NovelReleaseOperator),
  status: NovelReleaseStatus,
  error_code: v.nullable(v.string()),
  error_message: v.nullable(v.string()),
  stats: NovelReleaseStats,
  created_at: v.string(),
  updated_at: v.string(),
  logs: v.array(NovelReleaseLog),
})

/**
 * 发布区里的一条 outbound 记录：stale 为进行中且 updated_at 早于传输超时，即进程在发布中重启留下的卡住记录，
 * 管理员可经收尾入口把它置 failed（R-4）。
 */
export const NovelReleaseStateRecord = v.object({
  ...NovelReleaseRecord.entries,
  stale: v.boolean(),
})

/** 查询 Main 失败的原因：code 是原因码（Main 拒绝时是它自己给的码），message 是原文，页面按码翻译、原文放 title（R-6）。 */
export const NovelReleaseConnectionError = v.object({
  code: v.pipe(v.string(), v.minLength(1)),
  message: v.string(),
})

/**
 * GET /api/novel/admin/packs/:id/release：configured 为 Stage 配了目标地址与共享凭据；connected 为本次查询 Main 成功，
 * 失败原因在 connection_error；main 是 Main 上同 slug 作品（见 NovelReleaseTargetState），未连通或 Main 还没有这部作品时为 null；
 * records 是这部作品最近的 outbound 记录（新的在前，带日志）。
 */
export const NovelReleaseState = v.object({
  configured: v.boolean(),
  connected: v.boolean(),
  target_url: v.nullable(v.string()),
  connection_error: v.nullable(NovelReleaseConnectionError),
  main: v.nullable(v.object({
    release_managed: v.boolean(),
    current_version: v.nullable(v.string()),
    released_at: v.nullable(v.string()),
  })),
  records: v.array(NovelReleaseStateRecord),
})

/** 发布结果：Stage 后台接口与 Main 内部接口同形；同一 release_id 重放返回原回执。 */
export const NovelReleaseResult = v.object({
  ok: v.literal(true),
  release_id: ReleaseId,
  version: v.string(),
  assets_copied: Count,
  assets_reused: Count,
  bytes_copied: Count,
})

/**
 * 发布包里的一个存储对象：版本内容与封面引用到的 Stage 存储对象，按 object_key 去重。kind 与 tag 是 Stage 的素材
 * 登记（素材种类与登记 id），内容引用了未在本作品登记的对象时为 null；width、height 是登记时记下的像素尺寸，
 * 没记或未登记为 null，Main 镜像素材行时原样写入；public_url 是它在 Stage 内容里的地址，Main 从这里下载并按
 * size 与 hash 校验。
 */
export const NovelReleaseAsset = v.strictObject({
  kind: v.nullable(v.picklist(['cover', 'location', 'state_image', 'item'])),
  tag: v.nullable(Id),
  object_key: v.pipe(v.string(), v.minLength(1), v.maxLength(512)),
  public_url: v.pipe(v.string(), v.url()),
  size: Count,
  hash: Sha256,
  mime: v.pipe(v.string(), v.minLength(1), v.maxLength(255)),
  width: v.nullable(Count),
  height: v.nullable(Count),
})

/**
 * 发布包本体：Stage 存储的公共根、作品标识、版本（内容里的 Stage 与历史存储地址由 Main 改写成自己的存储地址）、
 * 音色映射与存储对象清单。Main 改写后按 source_storage_base_url 与对象地址的根复核内容里不再有来源存储引用（R-3）。
 * 两端都经它解析后再算 checksum，键序一致。
 */
export const NovelReleaseBundle = v.strictObject({
  source_storage_base_url: v.pipe(v.string(), v.url()),
  pack: v.strictObject({
    slug: v.pipe(v.string(), v.minLength(1), v.maxLength(96)),
    title: v.pipe(v.string(), v.minLength(1), v.maxLength(200)),
    language: v.nullable(v.pipe(v.string(), v.maxLength(16))),
    cover_object_key: v.nullable(v.pipe(v.string(), v.minLength(1), v.maxLength(512))),
  }),
  version: v.strictObject({
    version: v.pipe(v.string(), v.minLength(1), v.maxLength(64)),
    note: v.nullable(v.pipe(v.string(), v.maxLength(500))),
    package_hash: v.pipe(v.string(), v.minLength(1), v.maxLength(128)),
    content_json: v.record(v.string(), v.unknown()),
    play_config_json: NovelPlayConfig,
    play_config_hash: v.string(),
    source_revision: v.nullable(v.number()),
    created_at: v.string(),
  }),
  assets: v.array(NovelReleaseAsset),
})

/** POST /api/internal/novel/releases（Bearer 共享凭据）：bundle_checksum 是发布包本体（JSON 序列化）的 SHA-256。 */
export const NovelReleaseRequest = v.strictObject({
  release_id: ReleaseId,
  bundle_checksum: Sha256,
  ...NovelReleaseBundle.entries,
})

/**
 * GET /api/internal/novel/releases/packs/:slug：Main 上同 slug 作品。release_managed 为这部作品由发布通道建立
 * （owner_user_id 为空）；创作者在 Main 自建的同 slug 作品为 false，发布会被拒（release_slug_taken），这时不报它的版本（R-13）。
 * current_version 与 released_at 是发布通道作品的当前版本与最近一次发布套用时间。
 */
export const NovelReleaseTargetState = v.object({
  exists: v.boolean(),
  release_managed: v.boolean(),
  current_version: v.nullable(v.string()),
  released_at: v.nullable(v.string()),
})
